import LoadingSpinner from "@/components/common/LoadingSpinner";
import { CustomChildTable } from "@/components/CustomChildTable";
import { CustomImageUpload } from "@/components/CustomImageUpload";
import { CustomFileUpload } from "@/components/CustomFileUpload";
import { get } from "@/services/axiosUitils";
import {
  Checkbox,
  DatePicker,
  FormGrid,
  FormItem,
  FormLayout,
  FormTab,
  Input,
  NumberPicker,
  Password,
  Radio,
  Select,
  Switch,
  TimePicker,
  TreeSelect,
} from "@formily/antd-v5";
import { createSchemaField, FormProvider } from "@formily/react";
import {
  Card,
  Cascader,
  InputNumber,
  Rate,
  Slider,
  Tabs,
  Transfer,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { translateSchemaDeep } from "@/lib/translateSchemaDeep";
import GoogleAddressInput from "@/components/GoogleAddressInput";

const Text = ({ value, content, mode = "span", style = {}, ...props }: any) => {
  const Tag = mode ?? "span";
  return (
    <Tag style={style} {...props}>
      {content ?? value}
    </Tag>
  );
};

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    NumberPicker,
    Password,
    Select,
    Checkbox,
    DatePicker,
    Switch,
    Radio,
    CustomFileUpload,
    GoogleAddressInput,
    CustomImageUpload,
    FormLayout,
    Card,
    FormGrid,
    FormTab,
    Tabs,
    InputNumber,
    TreeSelect,
    TimePicker,
    Cascader,
    Transfer,
    Slider,
    Rate,
    TextArea,
    CustomChildTable,
    Text,
    ReactQuill,
  },
});

const SchemaFormRender = ({ schemaJson, form }: any) => {
  const { t, i18n } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  // Derive translated schema without useEffect+setState (unstable `t` from i18next retriggered
  // that effect every render → "Maximum call stack size exceeded"). Omit `t` from deps: it
  // changes identity almost every render; `i18n.language` + `schemaJson` capture real updates.
  const translatedSchema = useMemo(() => {
    if (!schemaJson) {
      return null;
    }
    const schema = schemaJson.schema ?? schemaJson;
    return translateSchemaDeep(schema, t, "common");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` changes identity every render
  }, [schemaJson, i18n.language]);

  const useAsyncDataSource = useCallback(
    (service: (arg0: any) => Promise<any>, doc: any) => (field: any) => {
      field.loading = true;
      service(doc).then((data: any) => {
        field.dataSource = data;
        field.loading = false;
      });
    },
    []
  );

  const getOptions = useCallback(async (schemaName: string) => {
    return await get(`/dropdown/${schemaName}/`);
  }, []);

  // Stable `t` wrapper + stable scope object: a new scope every render can make Formily
  // re-run reactions in a loop → RangeError: Maximum call stack size exceeded.
  const stableT = useMemo(
    () => ((key: any, opts?: any) => tRef.current(key, opts)) as typeof t,
    []
  );

  const scope = useMemo(
    () => ({
      useAsyncDataSource,
      getOptions,
      t: stableT,
    }),
    [useAsyncDataSource, getOptions, stableT]
  );

  if (!schemaJson) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const layoutProps = schemaJson.form ?? {};

  return (
    <FormProvider form={form}>
      <FormLayout {...layoutProps}>
        <SchemaField schema={translatedSchema} scope={scope} />
      </FormLayout>
    </FormProvider>
  );
};

export default SchemaFormRender;
